import React, { useState } from "react";
import { Tabs, Space, Button, Row, Col, TabsProps, Typography, Dropdown } from "antd";

import type { ColumnsType } from "antd/es/table";
import { ProfileOutlined, EyeOutlined, EditOutlined, CarOutlined } from "@ant-design/icons";
import { useGetBookingsQuery } from "../../../redux/services/bookingAPI";
import TopScrollTable from "../../../components/TopScrollTable";
import Filters from "../Filters";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { getStatusText, isUserHasRoleTypes, localizeDate, getQuerySearchParams, applyPaginationToQueryParams } from "../../../utils";
import { useAppSelector } from "../../../redux/hooks";

const { Text } = Typography;

const items: TabsProps["items"] = [
  {
    key: "1",
    label: "All Bookings"
  },
  {
    key: "2",
    label: "Un-Assigned Bookings"
  }
];

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const Booking = () => {
  const querySearchParams = getQuerySearchParams();
  const [queryParams, setQueryParams] = useState<any>({
    include_all: true,
    page: parseInt(querySearchParams.page) || 1,
    page_size: parseInt(querySearchParams.page_size) || 20
  });
  const navigate = useNavigate();

  const { data, isLoading, isFetching } = useGetBookingsQuery({
    ...queryParams
  });
  const locations = data?.bookings || [];
  const user = useAppSelector<any>((store) => store.auth.user);
  const role_types = user.roles.map((role: any) => role.role_type);
  const isManager = !role_types
    .map((role_type: any) => ["city_manager", "zonal_manager"].includes(role_type))
    .includes(false);

  const isClient = user?.user_type === "client";

  const columns: ColumnsType<DataType> = [
    {
      title: "Brand Code",
      dataIndex: "brand",
      key: "brand_code",
      render: (brand) => <Text>{brand ? brand.code : "-"}</Text>
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      render: (zone) => <>{zone?.name}</>
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "Dealer eCB Booking Code",
      dataIndex: "ecb_booking_code",
      key: "ecb_booking_code"
    },
    {
      title: "City Manager",
      dataIndex: "assigned_city_manager",
      key: "assigned_city_manager",
      render: (assigned_city_manager) => (
        <Text>{assigned_city_manager ? assigned_city_manager?.name : "-"}</Text>
      )
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "Brand Booking Status",
      dataIndex: "brand_booking_status",
      key: "brand_booking_status"
    },
    {
      title: "eCB Rcvd Date",
      dataIndex: "ecb_receive_date",
      key: "ecb_receive_date",
      render: (ecb_receive_date) => (
        <Text>{ecb_receive_date ? localizeDate(ecb_receive_date) : "-"}</Text>
      )
    },
    {
      title: "First Contact Date",
      dataIndex: "first_contact_at",
      key: "first_contact_at",
      render: (first_contact_at) => <>{first_contact_at ? "Yes" : "No"}</>
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, booking: any) => <>{getStatusText(booking)}</>
    },
    {
      title: "Surveys",
      key: "surveys",
      dataIndex: "surveys",
      render: (surveys: any[]) => (
        <>
          {surveys?.length > 0 &&
            surveys?.map((survey) => (
              <Text style={{ whiteSpace: "nowrap" }} key={survey?.id}>
                <Link to={`/surveys?ecb_code=${survey.ecb_survey_code}`}>
                  {`${survey?.ecb_survey_code} - ${survey?.status.toUpperCase()}`}
                </Link>
                <br />
              </Text>
            ))}

          {surveys?.length === 0 && (
            <Text style={{ whiteSpace: "nowrap" }}>
              -
              <br />
            </Text>
          )}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, booking: any) => (
        <Space size="middle" direction="horizontal">
          <>
            <Link to={`${booking?.id}`}>
              <Button icon={<EyeOutlined />} title="View"></Button>
            </Link>
            <Link to={`${booking?.id}/update`}>
              <Button icon={<EditOutlined />} title="Update"></Button>
            </Link>
            <Link to={`${booking?.id}/create-survey`}>
              <Button icon={<ProfileOutlined />} title="Create Survey"></Button>
            </Link>
          </>
        </Space>
      )
    }
  ];

  const clientColumns: ColumnsType<DataType> = [
    {
      title: "Brand Code",
      dataIndex: "brand",
      key: "brand_code",
      render: (brand) => <Text>{brand ? brand.code : "-"}</Text>
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      render: (zone) => <>{zone?.name}</>
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "eCB Rcvd Date",
      dataIndex: "ecb_receive_date",
      key: "ecb_receive_date",
      render: (ecb_receive_date) => (
        <Text>{ecb_receive_date ? localizeDate(ecb_receive_date) : "-"}</Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, booking: any) => <>{getStatusText(booking)}</>
    },

    {
      title: "Surveys",
      key: "surveys",
      dataIndex: "surveys",
      render: (surveys: any[]) => (
        <>
          {surveys?.length > 0 &&
            surveys?.map((survey) => (
              <Text style={{ whiteSpace: "nowrap" }} key={survey?.id}>
                <Link to={`/surveys?ecb_code=${survey.ecb_survey_code}`}>
                  {`${survey?.ecb_survey_code} - ${survey?.status.toUpperCase()}`}
                </Link>
                <br />
              </Text>
            ))}

          {surveys?.length === 0 && (
            <Text style={{ whiteSpace: "nowrap" }}>
              -
              <br />
            </Text>
          )}
        </>
      )
    },

    {
      title: "Action",
      key: "action",
      render: (_, booking: any) => (
        <Space size="middle" direction="horizontal">
          <Link to={`${booking?.id}`}>
            <Button icon={<EyeOutlined />} title="View"></Button>
          </Link>
        </Space>
      )
    }
  ];

  const onChange = (key: string) => {
    if (key === "1") {
      const values: any = { unassigned: null };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
    if (key === "2") {
      const values: any = { unassigned: true };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const pagination = {
    pageSize: parseInt(querySearchParams.page_size) || 20,
    current: parseInt(querySearchParams.page) || 1,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onChange: (page: number, pageSize: number) => {
      const values = {
        page,
        page_size: pageSize
      };

      setQueryParams((old: any) => ({ ...old, ...values }));

      applyPaginationToQueryParams({
        ...queryParams,
        ...values,
        navigate
      });
    }
  };

  const uploadMenuItems = [];

  if (!isManager || !isClient) {
    if (!isClient) {
      uploadMenuItems.push({
        key: "1",
        label: <Link to="upload-bookings">Upload Booking</Link>
      });
    }

    if (isUserHasRoleTypes(user, ["super_user"])) {
      uploadMenuItems.push({
        key: "2",
        label: <Link to="transfer-bookings">Transfer Bookings</Link>
      });
    }

    if (isUserHasRoleTypes(user, ["super_user", "mis"])) {
      uploadMenuItems.push({
        key: "3",
        label: <Link to="import-call-logs">Upload Call Logs</Link>
      });
    }

    uploadMenuItems.push({
      key: "4",
      label: <Link to="update-bookings-status">Update Status</Link>
    });

    if (isUserHasRoleTypes(user, ["super_user", "mis"])) {
      uploadMenuItems.push({
        key: "3",
        label: <Link to="update-pi-dates">Import PI Dates</Link>
      });
    }
  }

  return (
    <>
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Bookings</h2>
            <Text>Total bookings: {data?.meta?.total_count || 0}</Text>
          </div>
        </Col>
      </Row>

      <Row>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <Tabs defaultActiveKey="1" items={items} onChange={onChange} />
          {(!isManager || !isClient) && (
            <Dropdown menu={{ items: uploadMenuItems }}>
              <Button size="large" type="primary" icon={<CarOutlined />}>
                Upload Options
              </Button>
            </Dropdown>
          )}
        </Col>
      </Row>

      <Filters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
        bookings
      />

      <TopScrollTable
        rowKey="id"
        columns={isClient ? clientColumns : columns}
        dataSource={locations}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />
      <Outlet />
    </>
  );
};

export default Booking;
